// Kebab overflow menus (`<details class="pc-kebab">`).
//
// The menu is `position: fixed`, not `absolute`, because these menus sit inside
// `.pc-table-scroll`. That wrapper sets `overflow-x: auto`, and per CSS Overflow 3
// a `visible` value on the other axis computes to `auto` — so it is a scroll
// container that clips in *both* directions and cuts an absolutely-positioned
// menu off at the table's bottom edge. A fixed menu is positioned against the
// viewport instead, which nothing on the page clips, so we place it here.
//
// `<details>` fires `toggle` asynchronously, so open state is read from the DOM
// rather than cached in a variable — a cache fed by that event is stale for the
// rest of the current task.

const GAP = 4;

const openKebabs = () => document.querySelectorAll("details.pc-kebab[open]");
const menuOf = (details) => details.querySelector(".pc-kebab__menu");

function place(details) {
  const summary = details.querySelector("summary");
  const menu = menuOf(details);
  if (!summary || !menu) return;

  const anchor = summary.getBoundingClientRect();
  const { width, height } = menu.getBoundingClientRect();

  // Right-align to the button, then keep it inside the viewport.
  const left = Math.min(
    Math.max(GAP, anchor.right - width),
    Math.max(GAP, window.innerWidth - width - GAP)
  );

  // Below the button, flipping above only when it fits there and not below.
  const fitsBelow = window.innerHeight - anchor.bottom - GAP >= height;
  const fitsAbove = anchor.top - GAP >= height;
  const top = fitsBelow || !fitsAbove ? anchor.bottom + GAP : anchor.top - height - GAP;

  menu.style.left = `${Math.round(left)}px`;
  menu.style.top = `${Math.round(top)}px`;
  menu.dataset.placed = "1";
}

function close(details) {
  const menu = menuOf(details);
  if (menu) delete menu.dataset.placed;
  details.removeAttribute("open");
}

const closeAllExcept = (keep) => openKebabs().forEach((d) => d !== keep && close(d));

// Keep the open menu glued to its button while the page or the table scrolls.
// Bound only while a menu is open, so quiet pages pay nothing.
const reposition = () => openKebabs().forEach(place);
function trackScroll(on) {
  const fn = on ? "addEventListener" : "removeEventListener";
  document[fn]("scroll", reposition, { capture: true, passive: true });
  window[fn]("resize", reposition, { passive: true });
}

// `toggle` does not bubble, so listen on the way down.
document.addEventListener(
  "toggle",
  (e) => {
    const details = e.target;
    if (!(details instanceof HTMLElement) || !details.matches("details.pc-kebab")) return;
    if (details.open) {
      closeAllExcept(details);
      place(details);
      trackScroll(true);
    } else if (openKebabs().length === 0) {
      trackScroll(false);
    }
  },
  true
);

// `<details>` has no light-dismiss of its own: picking an item closes its menu,
// and a click anywhere outside an open kebab closes that one too.
document.addEventListener("click", (e) => {
  const inside = e.target.closest?.("details.pc-kebab") ?? null;
  const onItem = !!e.target.closest?.(".pc-kebab__item");
  openKebabs().forEach((d) => (d !== inside || onItem) && close(d));
});

document.addEventListener("keydown", (e) => {
  if (e.key === "Escape") openKebabs().forEach(close);
});
